import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBrandWhatsapp, IconMail } from '@tabler/icons-react';
import { formatPhone } from '@/lib/phone';
import {
  settingsEqual,
  settingsFromSubscriptions,
  subscriptionsFromSettings,
  validateSettings,
  type PriceAlertErrors,
} from '@/lib/priceAlert';
import { useUpdatePriceAlerts } from '@/hooks/usePriceAlert';
import type { PriceAlertSettings, Subscription } from '@/types/priceAlert';

interface BestPriceAlertModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly subscriptions: readonly Subscription[];
  /** Email из профиля — подставляем, если своего ещё нет. */
  readonly fallbackEmail: string;
}

// Настройка каналов уведомлений о лучшей цене продажи (WhatsApp + Email). Telegram/Max — на будущее.
// Подписка глобальная (по всем предложениям пользователя) — это явно сказано в интро (§5).
export default function BestPriceAlertModal({ opened, onClose, subscriptions, fallbackEmail }: BestPriceAlertModalProps) {
  const { t } = useTranslation();
  const update = useUpdatePriceAlerts();

  const initial = useMemo<PriceAlertSettings>(
    () => settingsFromSubscriptions(subscriptions, fallbackEmail),
    [subscriptions, fallbackEmail],
  );
  const [settings, setSettings] = useState<PriceAlertSettings>(initial);
  const [errors, setErrors] = useState<PriceAlertErrors>({});

  // Сброс формы к серверному состоянию при каждом открытии.
  useEffect(() => {
    if (opened) {
      setSettings(initial);
      setErrors({});
    }
  }, [opened, initial]);

  const dirty = !settingsEqual(settings, initial);

  const patch = (next: Partial<PriceAlertSettings>): void => setSettings((prev) => ({ ...prev, ...next }));

  const handleSave = (): void => {
    const found = validateSettings(settings, {
      required: t('auth.validation.required'),
      phone: t('auth.validation.phone'),
      email: t('auth.validation.email'),
    });
    setErrors(found);
    if (found.phone || found.email) {
      return;
    }
    update.mutate(subscriptionsFromSettings(settings), {
      onSuccess: () => {
        notifications.show({ color: 'lime', message: t('app.sell.priceAlert.saved') });
        onClose();
      },
      onError: () => notifications.show({ color: 'red', message: t('app.sell.priceAlert.saveFailed') }),
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} centered title={t('app.sell.priceAlert.title')} size="md">
      <Stack gap="lg">
        <Text size="sm" c="gray.8">
          {t('app.sell.priceAlert.intro')}
        </Text>

        <Stack gap="md">
          <div>
            <Checkbox
              label={
                <Group gap={6} wrap="nowrap">
                  <IconBrandWhatsapp size={18} color="var(--mantine-color-brand-6)" />
                  <Text fw={500}>{t('app.sell.priceAlert.whatsapp')}</Text>
                </Group>
              }
              checked={settings.whatsappEnabled}
              onChange={(event) => patch({ whatsappEnabled: event.currentTarget.checked })}
            />
            <TextInput
              mt="xs"
              label={t('app.sell.priceAlert.phoneLabel')}
              value={settings.phone}
              onChange={(event) => patch({ phone: formatPhone(event.currentTarget.value) })}
              error={errors.phone}
              disabled={!settings.whatsappEnabled}
              inputMode="tel"
              placeholder="+7 (___) ___-__-__"
            />
          </div>

          <div>
            <Checkbox
              label={
                <Group gap={6} wrap="nowrap">
                  <IconMail size={18} color="var(--mantine-color-brand-6)" />
                  <Text fw={500}>{t('app.sell.priceAlert.email')}</Text>
                </Group>
              }
              checked={settings.emailEnabled}
              onChange={(event) => patch({ emailEnabled: event.currentTarget.checked })}
            />
            <TextInput
              mt="xs"
              label={t('app.sell.priceAlert.emailLabel')}
              value={settings.email}
              onChange={(event) => patch({ email: event.currentTarget.value })}
              error={errors.email}
              disabled={!settings.emailEnabled}
              inputMode="email"
              placeholder={t('app.sell.priceAlert.emailPlaceholder')}
            />
          </div>
        </Stack>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} loading={update.isPending} disabled={!dirty}>
            {t('app.profile.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
